/** @jsxImportSource @emotion/react */
/* eslint-disable react/no-unused-prop-types */
// see - https://www.figma.com/file/pv2HV6DTSKY612VWeYWYdL/CE22-Design-System?node-id=48%3A264
import type { CSSProperties, PropsWithChildren, ReactNode } from 'react';
import React from 'react';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormHelperText from '@mui/material/FormHelperText';
import type { SxProps, Theme as MuiTheme, InputProps } from '@mui/material';
import { Box } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type { CSSObject, Interpolation, Theme } from '@emotion/react';
import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';
import type { ITheme, ThemeColor } from '../../styles/theme';
import Typography, { TypographyVariant } from '../Typography';
import type { IPropsWithTestId } from '../../../constants/data-element-tracking-ids';
import {
  ELEMENT_ID_DATA_LOG_ATTRIBUTE,
  ELEMENT_ID_DATA_TEST_ATTRIBUTE,
} from '../../../constants/data-element-tracking-ids';

export const INPUT_VERTICAL_GAP_BETWEEN_ELEMENTS = '8px';
export const INPUT_HORIZONTAL_GAP_BETWEEN_ELEMENTS = '8px';
export const INPUT_HEIGHT = '40px';
export const INPUT_PADDING = '10px';
export const INPUT_ADORNMENT_ICON_HEIGHT_PX = 14;

type AdornmentType = string | IconDefinition | ReactNode | undefined;

export const BaseErrorMessage: React.FC<
PropsWithChildren<
IPropsWithTestId & {
  theme: ITheme;
  withoutTopGap?: boolean;
}
>
> = ({
  theme, withoutTopGap = false, children, dataElementId,
}) => (
  <Typography
    theme={theme}
    variant={TypographyVariant.SMALL_BODY_BOLD}
    color={theme.colors.negative}
    additionalStyles={withoutTopGap ? undefined : { marginTop: '8px' }}
    dataElementId={dataElementId}
  >
    {children}
  </Typography>
);

const isIconDefinition = (
  adornment: AdornmentType,
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
): adornment is IconDefinition => !!(adornment as IconDefinition).icon;

const renderInputAdornment = (
  adornment: AdornmentType,
  testId: string,
  theme: ITheme,
  iconStyles?: CSSObject,
  iconStringStyles?: CSSObject,
): ReactNode | undefined => {
  if (adornment === undefined) {
    return undefined;
  }

  const inactiveColor = theme.colors.grayscale.inactiveGrey1;

  const baseIconStyles: Interpolation<Theme> = {
    fontSize: `${INPUT_ADORNMENT_ICON_HEIGHT_PX}px`,
    fontWeight: 900,
    lineHeight: '18.2px',
    color: inactiveColor,
    WebkitTextFillColor: inactiveColor,
  };

  const baseIconStringStyles: Interpolation<Theme> = {
    fontSize: '16px',
    fontWeight: 400,
    whiteSpace: 'nowrap',
    color: inactiveColor,
    WebkitTextFillColor: inactiveColor,
  };

  if (isIconDefinition(adornment)) {
    return (
      <FontAwesomeIcon
        icon={adornment}
        css={[baseIconStyles, iconStyles]}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...{ [ELEMENT_ID_DATA_TEST_ATTRIBUTE]: testId }}
      />
    );
  }

  if (typeof adornment === 'string') {
    return (
      <Typography
        theme={theme}
        additionalStyles={[baseIconStringStyles, iconStringStyles]}
      >
        {adornment}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        ...baseIconStringStyles,
        ...iconStringStyles,
        svg: { ...baseIconStyles, ...iconStyles },
      }}
    >
      {adornment}
    </Box>
  );
};

export enum InputVariant {
  Outlined,
  Signature,
}

export interface IInputBaseProps<T> extends IPropsWithTestId {
  id?: string;
  defaultValue?: T;
  value?: T;
  onChange: (newVal: T) => void;
  placeholderText?: string;
  label?: ReactNode;
  helperText?: ReactNode;
  customError?: ReactNode;
  alert?: 'error' | 'success';
  startAdornmentElement?: AdornmentType;
  endAdornmentElement?: AdornmentType;
  variant?: InputVariant;
  fullWidth?: boolean;
  theme: ITheme;
  additionalStyles?: SxProps<MuiTheme>;
}

export type InputPropsToExcludeFromMuiProps = keyof Pick<
InputProps,
| 'id'
| 'value'
| 'onChange'
| 'onSelect'
| 'placeholder'
| 'error'
| 'startAdornment'
| 'endAdornment'
| 'fullWidth'
| 'sx'
| 'style'
>;

export type RenderInputAdornmentType = (
  adornmentElement: AdornmentType,
  testId?: string,
) => ReactNode;

interface IRenderInputProps {
  id: string;
  customError?: ReactNode;
  renderStartAdornment: RenderInputAdornmentType;
  renderEndAdornment: RenderInputAdornmentType;
  inputStyles: (extraStyles?: CSSProperties) => CSSProperties;
}

interface IInputControlProps<T> extends IInputBaseProps<T> {
  renderInput: (props: IRenderInputProps) => ReactNode;
}

const InputControlWrapper = <T extends string>({
  dataElementId,
  id = `${dataElementId}-control`,
  value,
  label,
  helperText,
  customError,
  alert = customError ? 'error' : undefined,
  startAdornmentElement,
  variant = InputVariant.Outlined,
  fullWidth,
  theme,
  additionalStyles,
  renderInput,
  ...props
}: IInputControlProps<T>) => {
  const endAdornmentStylesByAlert = {
    error: {
      iconColor: theme.colors.negative,
      icon: faXmark,
    },
    success: {
      iconColor: theme.colors.positive,
      icon: faCheck,
    },
  };

  const borderColor = alert
    ? endAdornmentStylesByAlert[alert].iconColor
    : theme.colors.grayscale.outlineGrey;
  const placeholderColor = theme.colors.grayscale.inactiveGrey1;
  const focusBackground = theme.colors.grayscale.white;
  const disabledBackground = theme.colors.grayscale.inactiveGrey2;

  const inputFormStyles = {
    width: '100%',
    transition: '.1s',
    backgroundColor: theme.colors.grayscale.white,
    color: value ? theme.colors.secondary : placeholderColor,
    '&.Mui-focused': {
      backgroundColor: focusBackground,
      borderColor: theme.colors.neutral,
      color: theme.colors.secondary,
      svg: {
        color: 'inherit',
      },
    },
    '&.Mui-disabled': {
      opacity: 'unset',
      backgroundColor: disabledBackground,
      borderColor: disabledBackground,
      color: placeholderColor,
      WebkitTextFillColor: placeholderColor,
      ':hover': {
        borderColor: disabledBackground,
      },
    },
  };

  const inputStyles = ({ borderRadius }: { borderRadius?: string }) => ({
    padding: 0,
    borderRadius,
    '&::placeholder': {
      color: placeholderColor,
      opacity: 1,
    },
    ':focus': {
      backgroundColor: focusBackground,
      borderRadius,
    },
    '&.Mui-disabled': {
      backgroundColor: disabledBackground,
    },
  });

  const settingsByVariant = {
    [InputVariant.Outlined]: {
      inputCommonStyles: (extraStyles?: CSSProperties) => ({
        ...inputFormStyles,
        fontFamily: 'Barlow, sans-serif',
        fontSize: '16px',
        fontWeight: 400,
        lineHeight: '140%',

        height: '40px',
        border: '1px solid',
        borderRadius: '5px',
        borderColor,
        '&:hover': {
          borderColor: theme.colors.secondary,
          color: theme.colors.secondary,
        },
        '&.MuiInputBase-root, .MuiInputBase-root': {
          // Paddings between elements inside inputs. Inputs/selects should be indented
          // so that they appear nicely in native elements.
          // For example, this affects paddings of options in Native select
          '> input:not([aria-hidden="true"]), > select, > div, > button, > svg':
            {
              '&:first-child': {
                paddingLeft: extraStyles?.paddingLeft ?? INPUT_PADDING,
              },
              '&:not(:last-child)': {
                paddingRight: INPUT_HORIZONTAL_GAP_BETWEEN_ELEMENTS,
              },
              '&:last-child': {
                paddingRight: extraStyles?.paddingRight ?? INPUT_PADDING,
              },
              // Input should have padding on the left and right
              // so as not to reduce the width of the active area.
              ...(startAdornmentElement
                ? {
                  // startAdornmentElement
                  '&:nth-child(1)': {
                    paddingRight: 0,
                  },
                  // input
                  '&:nth-child(2)': {
                    paddingLeft: INPUT_HORIZONTAL_GAP_BETWEEN_ELEMENTS,
                  },
                }
                : {}),
            },
        },
        '& .MuiInputBase-input': {
          display: 'flex',
          alignItems: 'center',
          alignContent: 'center',
          height: '100%',
          width: '100%',
          boxSizing: 'border-box',
          ...inputStyles({ borderRadius: '5px' }),
          paddingTop: extraStyles?.paddingTop,
          paddingBottom: extraStyles?.paddingBottom,
        },
        ...extraStyles,
        paddingLeft: undefined,
        paddingRight: undefined,
        paddingTop: undefined,
        paddingBottom: undefined,
      }),
      showEndAdornmentForAlert: true,
      helperText: {
        variant: TypographyVariant.DISCLAIMER,
        color: theme.colors.grayscale.inactiveGrey1,
      },
    },
    [InputVariant.Signature]: {
      inputCommonStyles: (extraStyles?: CSSProperties) => ({
        ...inputFormStyles,
        fontFamily: 'Dancing Script',
        fontSize: '32px',
        fontWeight: '400',
        lineHeight: '180%',
        letterSpacing: '-0.04em',
        textIndent: '7px',

        height: '58px',
        borderBottom: `1px solid ${borderColor}`,
        '& .MuiInputBase-input': {
          textIndent: '7px',
          ...inputStyles({}),
        },
        ...extraStyles,
      }),
      showEndAdornmentForAlert: false,
      helperText: {
        variant: TypographyVariant.SMALL_BODY_REGULAR,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        color: '#404040' as ThemeColor,
      },
    },
  };

  const renderStartAdornment = (
    startAdornment: AdornmentType,
    testId = 'start-icon',
  ) => renderInputAdornment(startAdornment, testId, theme);

  const renderEndAdornment = (
    endAdornmentElement: AdornmentType,
    testId = 'end-icon',
  ) =>
    renderInputAdornment(
      endAdornmentElement ||
        (settingsByVariant[variant].showEndAdornmentForAlert && alert
          ? endAdornmentStylesByAlert[alert].icon
          : undefined),
      testId,
      theme,
      {
        ...(alert ? { color: endAdornmentStylesByAlert[alert].iconColor } : {}),
      },
    );

  return (
    <FormControl
      variant="standard"
      sx={{
        '&.MuiFormControl-root': {
          display: 'flex',
          height: '100%',
          width: fullWidth ? '100%' : '200px',
          justifyContent: 'flex-start',
          '> label, > div': {
            margin: 0,
            '&:not(:last-child)': {
              marginBottom: INPUT_VERTICAL_GAP_BETWEEN_ELEMENTS,
            },
          },
        },
        ...additionalStyles,
      }}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...{
        [ELEMENT_ID_DATA_LOG_ATTRIBUTE]: dataElementId,
        [ELEMENT_ID_DATA_TEST_ATTRIBUTE]: dataElementId,
      }}
    >
      {/* Need to set position: relative on the label or it gets
      position: absolute from somewhere and looks messed up.
      overflow: visible is required for the label to not be shrunk to 0 height
      by the flex parent in some situations. */}
      {!!label && (
        <InputLabel
          shrink
          htmlFor={id}
          sx={{
            position: 'relative',
            overflow: 'visible',
            transform: 'none',
            '[role="tooltip"]': { whiteSpace: 'normal' },
          }}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...{ [ELEMENT_ID_DATA_TEST_ATTRIBUTE]: `${dataElementId}-label` }}
        >
          <Typography variant={TypographyVariant.SMALL_BODY_BOLD} theme={theme}>
            {label}
          </Typography>
        </InputLabel>
      )}
      {renderInput({
        customError,
        renderStartAdornment,
        renderEndAdornment,
        inputStyles: settingsByVariant[variant].inputCommonStyles,
        id,
        ...props,
      })}
      {!!helperText && (
        <FormHelperText component="div">
          <Typography
            variant={settingsByVariant[variant].helperText.variant}
            color={settingsByVariant[variant].helperText.color}
            theme={theme}
            dataElementId={`${dataElementId}-helper`}
          >
            {helperText}
          </Typography>
        </FormHelperText>
      )}
      {!!customError && (
        <FormHelperText component="div">
          <BaseErrorMessage
            theme={theme}
            withoutTopGap
            dataElementId={`${dataElementId}-error`}
          >
            {customError}
          </BaseErrorMessage>
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default InputControlWrapper;
